import { useEffect, useState } from 'react';
import {
  MONITOR_DURATIONS,
  cancelPacketMonitor,
  clearPacketMonitor,
  getMonitor,
  startPacketMonitor,
  subscribePacketMonitor,
  type GetSignaling,
} from '../../services/packetMonitor';
import { diagnoseChain } from './packetChain';

export interface PacketMonitorPanelProps {
  cameraId: string;
  /** Live getter for the transport: local = direct SFU WS, cloud = tunnelled. */
  getSignaling: GetSignaling;
  /** Hide entirely when the app's debug mode is off. */
  enabled?: boolean;
  className?: string;
}

const fmt = (v: any) => (v == null ? '—' : Math.round(v).toLocaleString());
const pctStr = (v: any) => (v == null ? '—' : `${v}%`);

/**
 * End-to-end packet ledger for one camera, camera → browser.
 *
 * One implementation for both dashboards. The only thing that differs between
 * them is how the signaling socket is reached, and that arrives as a prop.
 */
export default function PacketMonitorPanel({
  cameraId,
  getSignaling,
  enabled = true,
  className = '',
}: PacketMonitorPanelProps) {
  const [, setTick] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => subscribePacketMonitor(() => setTick(t => t + 1)), []);

  if (!enabled) return null;

  const monitor = getMonitor(cameraId);
  const isRunning =
    !!monitor && ['running', 'starting', 'collecting'].includes(monitor.state);
  const hasReport = !!monitor && monitor.state === 'done' && monitor.report;

  const start = async (seconds: number) => {
    try {
      await startPacketMonitor(cameraId, getSignaling, seconds);
    } catch {
      /* session records its own error */
    }
  };

  // ── Idle ───────────────────────────────────────────────────────────────
  if (!isRunning && !hasReport) {
    return (
      <div className={`px-3 py-1.5 border-t border-border/50 bg-muted/20 ${className}`}>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-mono text-muted-foreground mr-1">📡 PKT</span>
          {MONITOR_DURATIONS.map(d => (
            <button
              key={d.seconds}
              onClick={e => { e.stopPropagation(); start(d.seconds); }}
              className="px-2 py-0.5 text-[9px] font-medium rounded border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/15 transition-colors"
            >
              {d.label}
            </button>
          ))}
          {monitor?.error && <span className="text-[9px] text-red-400 ml-1">{monitor.error}</span>}
        </div>
      </div>
    );
  }

  // ── Running ────────────────────────────────────────────────────────────
  if (isRunning && monitor) {
    const pl: any = monitor.pipelineRunning;
    const sf: any = monitor.sfuRunning;
    const fe: any = monitor.feRunning;
    // A stage with no reading shows "—", never 0: "never measured" and
    // "received nothing" are opposite diagnoses.
    const cells = [
      { label: '📷 RTSP In', sub: 'pkts from cam', v: fmt(pl?.rtsp_packets), tone: 'text-pink-300' },
      { label: '📹 Pipe Out', sub: 'RTP → SFU', v: fmt(pl?.rtp_sent), tone: 'text-violet-300' },
      { label: '📥 SFU Recv', sub: 'pkts', v: fmt(sf?.packets_received), tone: 'text-cyan-300' },
      { label: '📤 SFU Sent', sub: 'pkts', v: fmt(sf?.packets_sent), tone: 'text-blue-300' },
      { label: '🌐 FE Recv', sub: 'pkts', v: fmt(fe?.packetsReceived), tone: 'text-emerald-300' },
      { label: '🎞 FE Decoded', sub: 'frames', v: fmt(fe?.framesDecoded), tone: 'text-teal-300' },
    ];
    return (
      <div className={`px-3 py-1.5 border-t border-cyan-500/30 bg-cyan-500/5 ${className}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-mono text-cyan-400">📡 Monitoring {monitor.durationSec}s…</span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-muted-foreground">
              {(monitor.progressPercent || 0).toFixed(0)}%
            </span>
            <button
              onClick={() => cancelPacketMonitor(cameraId)}
              className="text-[9px] px-1.5 py-0.5 rounded border border-red-500/40 text-red-400 hover:bg-red-500/15 transition-colors"
            >
              Stop
            </button>
          </div>
        </div>
        <div className="h-1 bg-muted rounded-full overflow-hidden mb-1">
          <div className="h-full bg-cyan-500 transition-all duration-300" style={{ width: `${monitor.progressPercent || 0}%` }} />
        </div>
        <div className="grid grid-cols-6 gap-x-0.5 text-[8px] font-mono text-center">
          {cells.map(c => <div key={c.label} className="text-muted-foreground">{c.label}</div>)}
          {cells.map(c => <div key={c.label + 'v'} className={`${c.tone} text-[10px]`}>{c.v}</div>)}
          {cells.map(c => <div key={c.label + 's'} className="text-muted-foreground/60 text-[7px]">{c.sub}</div>)}
        </div>
        {pl == null && (
          <div className="text-[8px] font-mono text-amber-400/80 mt-0.5 text-center">
            pipeline counters unavailable — SFU cannot reach ingest-live stats
          </div>
        )}
      </div>
    );
  }

  // ── Report ─────────────────────────────────────────────────────────────
  if (!monitor) return null;
  const report = monitor.report;
  const diag = diagnoseChain(report);
  const sevColor =
    diag?.severity === 'bad' ? 'text-red-400'
      : diag?.severity === 'warn' ? 'text-amber-400'
        : 'text-emerald-400';

  const copyReport = () => {
    const lines = [
      `📊 Packet Monitor — camera ${cameraId.slice(0, 8)}`,
      `Duration: ${report.actual_duration_sec?.toFixed(0)}s`,
      report.pipeline?.transcoding
        ? `Pipeline: TRANSCODING ${report.pipeline.codec_source || '?'}→${report.pipeline.codec_output || '?'} — the encoder reduces bytes on purpose; stages after it are measured against its output, not against the camera.`
        : 'Pipeline: passthrough — every stage should carry ~100% of the camera bytes.',
      monitor.windowFallback
        ? `⚠ SFU completion message never arrived — browser measured over ${monitor.durationSec + 15}s vs the SFU's ${monitor.durationSec}s, so the browser hop is inflated.`
        : '',
      '',
      `VERDICT: ${diag?.verdict ?? 'n/a'}`,
      ...(diag?.hints || []).map(h => `  • ${h}`),
      '',
      '── Chain (camera → browser) ──',
      ...(diag?.stages || []).map(
        st => `${st.isLoss ? '✗' : ' '} ${st.label.padEnd(24)} ${fmt(st.value).padStart(10)} ${st.unit.padEnd(7)} ${pctStr(st.survivalPct).padStart(7)}${st.note ? `  (${st.note})` : ''}`,
      ),
    ].filter(Boolean).join('\n');
    try {
      navigator.clipboard?.writeText(lines).then(
        () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
        () => { /* clipboard blocked */ },
      );
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className={`px-3 py-1.5 border-t border-border/50 bg-muted/20 space-y-1 ${className}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-mono ${sevColor}`}>
          📡 {report.actual_duration_sec?.toFixed(0)}s · {diag?.severity?.toUpperCase()}
          {report.pipeline?.transcoding && (
            <span className="text-muted-foreground/70 ml-1">
              · transcode {report.pipeline.codec_source}→{report.pipeline.codec_output}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5">
          <button onClick={copyReport} className="text-[9px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:bg-muted transition-colors">
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
          <button onClick={() => clearPacketMonitor(cameraId)} className="text-[9px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:bg-muted transition-colors">
            Clear
          </button>
          {MONITOR_DURATIONS.map(d => (
            <button key={d.seconds} onClick={() => start(d.seconds)} className="text-[9px] px-1.5 py-0.5 rounded border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/15 transition-colors">
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {monitor.windowFallback && (
        <div className="text-[8px] font-mono text-amber-400">
          ⚠ SFU completion never arrived — browser window {monitor.durationSec + 15}s vs SFU {monitor.durationSec}s; browser hop inflated.
        </div>
      )}

      {diag && (
        <div className={`text-[9px] font-mono ${sevColor} leading-snug`}>{diag.verdict}</div>
      )}

      {/* Full chain, one row per stage. This is the part that answers "where
          did the packets go" — the hop percentages alone cannot, because they
          all start downstream of the two places the stream actually degrades. */}
      <div className="font-mono text-[8px]">
        {diag?.stages.map(st => (
          <div
            key={st.key}
            className={`flex items-center justify-between gap-2 py-px ${st.isLoss ? 'text-red-400' : 'text-muted-foreground'}`}
          >
            <span className="truncate">{st.isLoss ? '✗ ' : ''}{st.label}</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className={st.isLoss ? 'text-red-300' : 'text-foreground/80'}>{fmt(st.value)}</span>
              <span className="text-muted-foreground/50 w-9 text-right">{st.unit}</span>
              <span className={`w-12 text-right ${st.isLoss ? 'text-red-300' : 'text-muted-foreground/70'}`}>
                {pctStr(st.survivalPct)}
              </span>
            </span>
          </div>
        ))}
      </div>

      {diag?.stages.some(st => st.note) && (
        <div className="text-[7px] font-mono text-muted-foreground/60 leading-snug">
          {diag.stages.filter(st => st.note).map(st => (
            <div key={st.key + 'n'}>{st.label}: {st.note}</div>
          ))}
        </div>
      )}

      {!!diag?.hints.length && (
        <div className="text-[8px] font-mono text-amber-300/90 leading-snug">
          {diag.hints.map((h, i) => <div key={i}>• {h}</div>)}
        </div>
      )}
    </div>
  );
}
